import { AuthCheck } from '@/components/auth-check.component';
import { Head } from '@/components/head.component';
import { OnboardingCheck } from '@/components/onboarding-check.component';
import { AutoTopupRow } from '@/components/plans/auto-topup-row.component';
import {
  PlansOverview,
  PlansOverviewSkeleton,
  type PlansOverviewProps,
} from '@/components/plans/plans-overview.component';
import { useCancelTrialRenewal } from '@/components/plans/use-cancel-trial-renewal.hook';
import { useCheckoutReturn } from '@/components/plans/use-checkout-return.hook';
import { useRenewPlan } from '@/components/plans/use-renew-plan.hook';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import { DraftReturn } from '@/containers/settings/billing/draft-return.component';
import { SettingsLayout } from '@/containers/settings/settings-layout.component';
import { useAppSumoMemberOffer } from '@/hooks/use-appsumo-upgrade.hook';
import { useBillingPortalLauncher } from '@/hooks/use-billing-portal.hook';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import {
  trackIntercomEvent,
  updateIntercomUser,
} from '@/hooks/use-intercom.hook';
import { useIsOrgAdmin } from '@/hooks/use-is-admin.hook';
import { useOrganizationActionFence } from '@/hooks/use-organization-action-fence.hook';
import { usePageRestored } from '@/hooks/use-page-restored.hook';
import { memberPriceLine } from '@/lib/appsumo-upgrade';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { canOfferCreditTopups } from '@/lib/billing-purchase-access';
import { planValueUsd, trackGoogleAdsConversion } from '@/lib/google-ads';
import { iconPartyHorn } from '@/lib/icons';
import { redirectToStripeCheckout } from '@/lib/stripe-redirect';
import { PLANS_SEARCH } from '@/lib/upgrade-link';
import { useAppDispatch } from '@/store';
import {
  billingApi,
  useCreateCreditTopupMutation,
  useGetBillingProfileQuery,
  useGetPlansQuery,
  useLazyGetBillingPortalQuery,
  useLazyGetCancelPortalQuery,
} from '@/store/features/billing.feature';
import {
  extensionApi,
  useGetOrganizationCreditsQuery,
} from '@/store/features/extension.feature';
import { useGetOnboardingChecklistQuery } from '@/store/features/onboarding-checklist.feature';
import { useUser } from '@clerk/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/plans')({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>
  ): {
    status: string | undefined;
    credits_added: number | undefined;
    plan_name: string | undefined;
  } => {
    return {
      status: search.status as string | undefined,
      credits_added: search.credits_added
        ? Number(search.credits_added)
        : undefined,
      plan_name: search.plan_name as string | undefined,
    };
  },
});

/** One toast for a plan change, so each status replaces the last. */
const PLAN_CHANGE_TOAST = 'plan-change';

function RouteComponent() {
  const navigate = useNavigate();
  // The post-checkout refresh retries outlive the effect that starts them:
  // it strips status=success from the URL 100ms in, which re-runs the effect
  // and would cancel them before the lagging webhook lands. Only unmount
  // clears these.
  const checkoutRetryTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => checkoutRetryTimersRef.current.forEach(clearTimeout),
    []
  );
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const { user } = useUser();
  const {
    trial,
    isReady: isFeatureAccessReady,
    error: featureAccessError,
    refetch: refetchFeatureAccess,
    organizationId,
  } = useFeatureAccess();
  const { beginOrganizationAction, isOrganizationActionCurrent } =
    useOrganizationActionFence(organizationId);
  const { status, credits_added, plan_name } = Route.useSearch();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [topupAmount, setTopupAmount] = useState<number>(500);
  const [isTopupProcessing, setIsTopupProcessing] = useState(false);

  useEffect(() => {
    setIsTopupProcessing(false);
  }, [organizationId]);

  // Browser Back from Checkout: the page returns with the spinner still on.
  usePageRestored(() => {
    setIsTopupProcessing(false);
  });

  // Handle checkout success redirect
  useEffect(() => {
    if (status !== 'success') return;

    // Only the new-subscription / top-up paths pass credits_added in the URL
    // (backend sets it on the checkout-complete redirect). Plan-change
    // modifications land here without it, so gate the marketing banner and
    // tracking on its presence but always refresh billing/credit caches.
    if (credits_added) {
      setShowSuccessMessage(true);

      bestEffortAnalytics(() =>
        posthog?.capture('payment_completed', {
          credits_added: credits_added,
          plan_name: plan_name,
          user_id: user?.id,
        })
      );
      // Google Ads "Paid Subscription". The success URL has no amount, so
      // the value is the public list price of the plan; the exact charge
      // reaches Google through the weekly offline upload from Stripe.
      trackGoogleAdsConversion('paid', {
        transactionId: `${user?.id ?? 'anon'}:${plan_name ?? 'topup'}:${credits_added}`,
        value: planValueUsd(plan_name),
      });
      bestEffortAnalytics(() => {
        trackIntercomEvent('completed-payment', {
          credits_added: credits_added ? Number(credits_added) : 0,
          plan_name: plan_name || '',
        });
        updateIntercomUser({ plan_tier: plan_name || '' });
      });
    } else {
      // A plan change has no banner: without this it landed in silence.
      toast.success('Plan updated', { id: PLAN_CHANGE_TOAST });
    }

    // Invalidate feature access so gating updates immediately, and invalidate
    // Credits so the balance reflects the new plan (or prorated charge) as
    // soon as the backend's invoice.paid webhook lands. The credit-sync
    // WebSocket handler will also push an update when the webhook fires, so
    // whichever arrives first wins.
    dispatch(
      billingApi.util.invalidateTags([
        'FeatureAccess',
        'BillingProfile',
        'Plans',
      ])
    );
    dispatch(extensionApi.util.invalidateTags(['Credits']));

    // Stripe's invoice.paid webhook for subscription_update can lag the
    // redirect by a few seconds. Re-invalidate a couple of times so the UI
    // catches up even if the WebSocket message is missed.
    checkoutRetryTimersRef.current.push(
      ...[2000, 5000, 10000].map((ms) =>
        setTimeout(() => {
          dispatch(extensionApi.util.invalidateTags(['Credits']));
          dispatch(billingApi.util.invalidateTags(['FeatureAccess', 'Plans']));
        }, ms)
      )
    );

    // Clean up URL after showing message
    const cleanupTimer = setTimeout(() => {
      navigate({ to: '/plans', search: PLANS_SEARCH, replace: true });
    }, 100);

    // Hide success message after 10 seconds
    const hideTimer = setTimeout(() => {
      setShowSuccessMessage(false);
    }, 10000);

    return () => {
      clearTimeout(cleanupTimer);
      clearTimeout(hideTimer);
    };
  }, [status, credits_added, plan_name, navigate, posthog, user?.id, dispatch]);

  useEffect(() => {
    if (status !== 'change_scheduled') return;
    toast.success('Plan change scheduled', {
      description:
        'Your current plan and credits stay available until the next renewal. The new plan starts then.',
    });
    dispatch(billingApi.util.invalidateTags(['Plans', 'BillingProfile']));
    navigate({ to: '/plans', search: PLANS_SEARCH, replace: true });
  }, [status, navigate, dispatch]);

  // Card-backed trial: the plan it becomes was switched (nothing charged).
  useEffect(() => {
    if (status !== 'renewal_changed') return;
    toast.success('Plan updated', {
      description:
        'Your trial keeps running. The new plan starts when it ends.',
    });
    navigate({ to: '/plans', search: PLANS_SEARCH, replace: true });
  }, [status, navigate]);

  const {
    currentData: availablePlans,
    error: plansError,
    refetch: refetchPlans,
  } = useGetPlansQuery({ organizationId }, { skip: !organizationId });
  // An inline plan change lands on ?status=payment_pending while Stripe still
  // has to confirm it to the API (a bank debit, or a webhook that hasn't
  // arrived). Replay: the page said nothing and kept offering the old plan,
  // so the customer left. Say it's in hand, drop the param, then refresh
  // billing and entitlements for a minute: "Plan updated" once the plan,
  // period, seats or an add-on move, or a note if they don't.
  const planSignature = availablePlans
    ? JSON.stringify([
        availablePlans.current_plan?.id,
        availablePlans.current_billing_interval,
        availablePlans.seats_paid,
        availablePlans.current_plan?.credit_addon_credits,
        availablePlans.current_plan?.sender_addon_quantity,
      ])
    : null;
  const [pendingPlanFrom, setPendingPlanFrom] = useState<string | null>(null);
  useEffect(() => {
    if (status !== 'payment_pending' || !planSignature || pendingPlanFrom)
      return;
    setPendingPlanFrom(planSignature);
    toast.message('Confirming your plan change', {
      id: PLAN_CHANGE_TOAST,
      description: 'This can take a minute. The page updates when it’s done.',
    });
    navigate({ to: '/plans', search: PLANS_SEARCH, replace: true });
  }, [status, planSignature, pendingPlanFrom, navigate]);
  useEffect(() => {
    if (!pendingPlanFrom || !planSignature) return;
    if (planSignature === pendingPlanFrom) return;
    toast.success('Plan updated', { id: PLAN_CHANGE_TOAST });
    setPendingPlanFrom(null);
  }, [pendingPlanFrom, planSignature]);
  useEffect(() => {
    if (!pendingPlanFrom || !organizationId) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      void refetchPlans();
      void refetchFeatureAccess();
      attempts += 1;
      if (attempts < 12) return;
      window.clearInterval(timer);
      toast.message('Your plan change is still being confirmed', {
        id: PLAN_CHANGE_TOAST,
        description:
          'It can take a few minutes, longer for a bank payment. Refresh this page later to see your new plan.',
        duration: 15000,
      });
      setPendingPlanFrom(null);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [pendingPlanFrom, organizationId, refetchPlans, refetchFeatureAccess]);

  // Stripe's portal, credit packs and cancelling need an org admin.
  const isOrgAdmin = useIsOrgAdmin();
  useCheckoutReturn({
    status,
    enabled: !!availablePlans,
    organizationId,
  });

  const {
    currentData: billingInfo,
    error: billingError,
    refetch: refetchBillingProfile,
  } = useGetBillingProfileQuery({ organizationId }, { skip: !organizationId });

  const { currentData: creditsData } = useGetOrganizationCreditsQuery(
    { organizationId },
    { skip: !organizationId }
  );

  // Only paying Stripe customers get a Cancel plan link. Free orgs have no
  // subscription to cancel, and an AppSumo lifetime licence never touches a
  // paid Stripe subscription, so its churn surface is elsewhere entirely.
  const hasPaidPlan =
    !!availablePlans?.current_plan?.tier_key &&
    availablePlans.current_plan.tier_key !== 'free';
  // Cancelled and running to its end: Renew replaces Cancel plan.
  const planEnding = Boolean(billingInfo?.ends_on);
  // Set by staff with no Stripe price: it ends on its own, nothing to cancel.
  const staffPlan = Boolean(billingInfo?.staff_plan_ends_on);
  // A Free org's People search runs on preview searches; credits don't add
  // any. Said beside the packs so a pack isn't bought to get past that wall.
  const topupNote =
    availablePlans?.current_plan?.tier_key === 'free'
      ? 'Credit packs pay for enrichment and reveals. People search needs a paid plan.'
      : null;
  // Onboarding bonus eligibility
  const { data: checklistData } = useGetOnboardingChecklistQuery();
  const showBonusBadge =
    checklistData?.eligible_for_onboarding_bonus === true &&
    checklistData?.onboarding_bonus_claimed === false;

  const [createCreditTopup] = useCreateCreditTopupMutation();

  const [triggerGetBillingPortal] = useLazyGetBillingPortalQuery();
  const { isPortalLoading, openBillingPortal: handleOpenPortal } =
    useBillingPortalLauncher(organizationId, triggerGetBillingPortal);

  const [triggerGetCancelPortal] = useLazyGetCancelPortalQuery();
  const [isCancelLoading, setIsCancelLoading] = useState(false);

  // Straight to Stripe's cancel screen. One click, no confirm dialog, no
  // interception: the confirm, the retention offer and the reason question are
  // all Stripe's job, and putting our own gate in front of cancellation is the
  // exact pattern CA AB 2863 and CO SB 25-145 prohibit.
  const handleCancelPlan = async () => {
    setIsCancelLoading(true);
    bestEffortAnalytics(() => posthog?.capture('cancel_flow_started'));
    try {
      const result = await triggerGetCancelPortal();
      if (result.data) {
        window.location.href = result.data.url;
        return;
      }
      throw new Error('No portal URL returned');
    } catch (error) {
      const message =
        (error as { data?: { detail?: string } })?.data?.detail ||
        (error as Error)?.message ||
        'Please try again.';
      toast.error(`Failed to open cancellation: ${message}`);
      setIsCancelLoading(false);
    }
  };

  // A plan that ended before Renew reached billing is chosen again in the
  // wizard.
  const {
    enabled: renewEnabled,
    renew: renewPlan,
    isLoading: isRenewing,
  } = useRenewPlan({
    renewsOn: billingInfo?.next_renewal_date,
    onEnded: () => {
      void navigate({ to: '/upgrade', search: { source: 'plans_renew' } });
    },
  });

  if (plansError || billingError || featureAccessError) {
    return (
      <div
        role='alert'
        className='flex min-h-64 flex-col items-center justify-center gap-3 text-center text-destructive'
      >
        <p>Failed to load billing information.</p>
        <Button
          variant='outline'
          onClick={() => {
            if (plansError) void refetchPlans();
            if (billingError) void refetchBillingProfile();
            if (featureAccessError) void refetchFeatureAccess();
          }}
        >
          Retry billing information
        </Button>
      </div>
    );
  }

  const handleTopupCheckout = async () => {
    const action = beginOrganizationAction();
    if (!action || isTopupProcessing || topupAmount < 100) return;
    setIsTopupProcessing(true);
    try {
      const response = await createCreditTopup({
        organizationId: action.organizationId,
        credits: topupAmount,
      }).unwrap();
      if (!isOrganizationActionCurrent(action)) return;
      const redirected = await redirectToStripeCheckout(response, () =>
        isOrganizationActionCurrent(action)
      );
      if (!isOrganizationActionCurrent(action)) return;
      if (!redirected) {
        setIsTopupProcessing(false);
      }
    } catch (error) {
      if (!isOrganizationActionCurrent(action)) return;
      const message =
        (error as { data?: { detail?: string } })?.data?.detail ||
        (error as Error).message;
      toast.error(message || 'Failed to create top-up session');
      setIsTopupProcessing(false);
    }
  };

  const canShowTopups = canOfferCreditTopups({
    organizationId,
    featureAccessReady: isFeatureAccessReady,
    featureAccessError: Boolean(featureAccessError),
    plansReady: Boolean(availablePlans),
    plansError: Boolean(plansError),
    trialActive: trial?.active,
  });

  // Every workspace gets the plans overview: legacy, v2 and AppSumo plans
  // included. Plans are bought in the guided wizard; credit packs open a
  // dialog.
  return (
    <>
      <Head heading='Billing & Plans' />
      <AuthCheck>
        <OnboardingCheck>
          <SettingsLayout>
            <DraftReturn />
            <div className='w-full pb-10'>
              {showSuccessMessage && credits_added && (
                <PaymentSuccessBanner credits={credits_added} />
              )}
              {availablePlans ? (
                <PlansOverviewWithTrialCancel
                  // A new org is a new page: an open cancel dialog closes
                  // rather than confirm against the org switched to.
                  key={organizationId}
                  catalog={availablePlans}
                  trial={trial}
                  credits={creditsData ? creditsData.count : null}
                  billing={billingInfo}
                  onOpenPortal={handleOpenPortal}
                  isPortalLoading={isPortalLoading}
                  canManageBilling={isOrgAdmin}
                  topup={{
                    // Billing refuses packs during a trial.
                    status: trial?.active
                      ? 'trial'
                      : canShowTopups
                        ? 'ready'
                        : 'checking',
                    note: topupNote,
                    amount: topupAmount,
                    onAmountChange: setTopupAmount,
                    isProcessing: isTopupProcessing,
                    onCheckout: () => void handleTopupCheckout(),
                  }}
                  cancelPlan={
                    hasPaidPlan && !planEnding && !staffPlan
                      ? {
                          onCancel: () => void handleCancelPlan(),
                          isLoading: isCancelLoading,
                        }
                      : null
                  }
                  renewPlan={
                    planEnding && renewEnabled
                      ? {
                          onRenew: () => void renewPlan(),
                          isLoading: isRenewing,
                        }
                      : null
                  }
                  cardTrial={!!(trial?.active && trial.renews_to)}
                  bonus={showBonusBadge}
                  autoTopup={
                    // Billing sells no credits during a trial.
                    organizationId && !trial?.active ? (
                      <AutoTopupRow organizationId={organizationId} />
                    ) : null
                  }
                />
              ) : (
                <PlansOverviewSkeleton />
              )}
            </div>
          </SettingsLayout>
        </OnboardingCheck>
      </AuthCheck>
    </>
  );
}

/**
 * The overview with a card trial's cancel wired in, so the cancel's mutation
 * only runs on the overview page, and an AppSumo-only workspace's member
 * price on its plan card while the offer is open.
 */
function PlansOverviewWithTrialCancel({
  cardTrial,
  ...props
}: Omit<PlansOverviewProps, 'cancelTrial' | 'memberPrice'> & {
  cardTrial: boolean;
}) {
  const { cancel, isLoading } = useCancelTrialRenewal();
  const { active, offer, prices } = useAppSumoMemberOffer();
  return (
    <PlansOverview
      {...props}
      memberPrice={
        active && offer && prices
          ? memberPriceLine(prices, offer.ends_at)
          : null
      }
      cancelTrial={
        cardTrial ? { onConfirm: () => void cancel(), isLoading } : null
      }
    />
  );
}

/** After a checkout that added credits: a banner over the page. */
function PaymentSuccessBanner({ credits }: { credits: number }) {
  return (
    <div className='fixed top-20 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 transform px-4'>
      <Alert className='border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'>
        <AppIcon
          icon={iconPartyHorn}
          className='h-4 w-4 text-green-600 dark:text-green-400'
        />
        <AlertTitle className='text-green-800 dark:text-green-200'>
          Payment Successful!
        </AlertTitle>
        <AlertDescription className='text-green-700 dark:text-green-300'>
          {credits.toLocaleString()} have been added to your account. Happy
          prospecting!
        </AlertDescription>
      </Alert>
    </div>
  );
}
